/**
 * Homepage: branching hero, product rows, collections run, band video, scroll motion.
 * Port of the redesign's home-branch.html scripts. Media URLs come from the
 * server (data attributes), so nothing is probed with extra requests.
 *
 * Motion rules: motion only clarifies, guides or gives feedback; reduced-motion
 * users get every end state without animation.
 */
const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
const mobileMQ = matchMedia('(max-width: 860px)');
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];

// ---------------------------------------------------------------------------
// Branching hero
// ---------------------------------------------------------------------------
function initHero() {
  const root = document.getElementById('bh');
  if (!root) return;
  const CFG = { RATE: 1.5, LEAD: 0.8, SPLIT_LEAD: 1.15, endFocus: { women: '40%', men: '73%' } };
  const d = root.dataset;
  const mobile = mobileMQ.matches;
  const halves = $('[data-bh-halves]', root);
  const btns = $$('.bh__half', root);
  const cards = Object.fromEntries($$('.bh__card', root).map((c) => [c.dataset.branch, c]));
  const films = Object.fromEntries($$('.bh__film', root).map((v) => [v.dataset.branch, v]));
  const panes = Object.fromEntries($$('.bh__pane', root).map((p) => [p.dataset.branch, p]));
  const back = $('[data-bh-back]', root);
  const other = (b) => (b === 'women' ? 'men' : 'women');
  let mode = d.mode;
  let open = null;
  let raf = 0;
  let cardTimer = 0;
  let settling = false;
  let kbd = false;
  let loaded = false;

  // Phones: use the 9:16 cuts when they exist; otherwise the split wipe with the end frames.
  if (mode === 'film' && mobile) {
    if (d.womenM && d.menM && d.startM) {
      films.women.dataset.src = d.womenM;
      films.men.dataset.src = d.menM;
    } else if (d.endWomen && d.endMen) {
      mode = 'split';
      root.dataset.mode = 'split';
      root.classList.add('is-studio');
      ['women', 'men'].forEach((b) => {
        const img = new Image();
        img.src = b === 'women' ? d.endWomen : d.endMen;
        img.alt = '';
        img.style.objectPosition = `${CFG.endFocus[b]} 30%`;
        $('.m', panes[b]).replaceChildren(img);
      });
    }
  }
  if (mode === 'film' && !films.women.dataset.src) {
    films.women.dataset.src = d.women;
    films.men.dataset.src = d.men;
  }

  // Films (about 1.7 MB each) download only on first interest in the hero (hover, focus,
  // touch), keeping first view under the 3 MB budget. The start image is frame 0, so the
  // hero looks identical before they arrive.
  function loadFilms() {
    if (loaded || mode !== 'film') return;
    loaded = true;
    Object.values(films).forEach((v) => {
      v.preload = 'auto';
      v.src = v.dataset.src;
      v.playbackRate = CFG.RATE;
      v.load();
    });
    films.women.classList.add('on'); // Home view = women film paused on frame 0 (same as the start image).
  }
  if (mode === 'film') {
    root.addEventListener('pointerenter', loadFilms, { once: true });
    root.addEventListener('touchstart', loadFilms, { once: true, passive: true });
    root.addEventListener('focusin', loadFilms, { once: true });
  }

  function showFilmAt0(b) {
    const v = films[b];
    const o = films[other(b)];
    if (v.classList.contains('on')) return;
    v.pause();
    v.currentTime = 0;
    v.classList.add('on');
    o.classList.remove('on'); // Both films open on the same frame, so the swap can't be seen.
  }

  function preview(b) {
    if (open || settling) return;
    btns.forEach((x) => x.classList.toggle('is-hot', x.dataset.branch === b));
    halves.classList.toggle('has-focus', !!b);
    if (b && mode === 'film' && loaded) showFilmAt0(b);
  }

  function showCard(b) {
    const c = cards[b];
    c.inert = false;
    c.classList.add('on');
    c.setAttribute('aria-hidden', 'false');
    setTimeout(() => $('a', c)?.focus({ preventScroll: true }), 500);
  }

  function choose(b) {
    if (open) return;
    loadFilms();
    open = b;
    preview(null);
    root.classList.add('is-open', `pick-${b}`);
    document.documentElement.classList.add('bh-open');
    try { history.pushState({ bh: b }, '', `#chase-${b}`); } catch (e) { /* history unavailable */ }

    if (mode === 'film') {
      const v = films[b];
      showFilmAt0(b);
      const finish = () => { v.currentTime = Math.max(0, (v.duration || 0) - 0.05); showCard(b); };
      if (reduce) {
        if (v.readyState >= 1) finish(); else v.addEventListener('loadedmetadata', finish, { once: true });
        return;
      }
      v.playbackRate = CFG.RATE;
      v.play().catch(() => showCard(b));
      const tick = () => {
        if (open !== b) return;
        if (v.duration && v.duration - v.currentTime <= CFG.LEAD) { showCard(b); return; }
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    } else {
      cardTimer = setTimeout(() => showCard(b), reduce ? 0 : CFG.SPLIT_LEAD * 1000);
    }
  }

  function goHome(fromPop) {
    if (!open) return;
    const b = open;
    open = null;
    cancelAnimationFrame(raf);
    clearTimeout(cardTimer);
    const c = cards[b];
    c.classList.remove('on');
    c.setAttribute('aria-hidden', 'true');
    c.inert = true;
    root.classList.remove('is-open', `pick-${b}`);
    document.documentElement.classList.remove('bh-open');
    if (mode === 'film') {
      const played = films[b];
      const next = films[other(b)];
      next.pause();
      next.currentTime = 0;
      next.classList.add('on');
      played.classList.remove('on');
      setTimeout(() => { played.pause(); played.currentTime = 0; }, 650);
    }
    settling = true;
    setTimeout(() => {
      settling = false;
      if (kbd) btns.find((x) => x.dataset.branch === b)?.focus({ preventScroll: true });
    }, 700);
    if (!fromPop) { try { if (history.state?.bh) history.back(); } catch (e) { /* ignore */ } }
  }

  btns.forEach((x) => {
    const b = x.dataset.branch;
    x.addEventListener('pointerenter', () => preview(b));
    x.addEventListener('focus', () => preview(b));
    x.addEventListener('click', (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey) return; // Let "open in new tab" go to the category.
      e.preventDefault();
      choose(b);
    });
  });
  halves.addEventListener('pointerleave', () => preview(null));
  halves.addEventListener('focusout', (e) => { if (!halves.contains(e.relatedTarget)) preview(null); });
  back.addEventListener('click', () => goHome(false));
  addEventListener('keydown', (e) => { kbd = true; if (e.key === 'Escape') goHome(false); });
  addEventListener('pointerdown', () => { kbd = false; });
  addEventListener('popstate', () => goHome(true));
}

// ---------------------------------------------------------------------------
// Product rows: arrow buttons
// ---------------------------------------------------------------------------
function initRails() {
  $$('.arrows').forEach((a) => {
    const rail = $(`#${a.dataset.rail} .rail`);
    if (!rail) return;
    $$('button', a).forEach((b) => b.addEventListener('click', () => {
      rail.scrollBy({ left: rail.clientWidth * 0.8 * Number(b.dataset.dir), behavior: reduce ? 'auto' : 'smooth' });
    }));
  });
}

// ---------------------------------------------------------------------------
// Statement band: video downloads when near the screen, plays only in view, can be paused.
// ---------------------------------------------------------------------------
function initBand() {
  const band = $('[data-band]');
  const v = $('[data-band-video]', band || document);
  if (!band || !v) return;
  const toggle = $('[data-band-toggle]', band);
  let userPaused = reduce;
  const setBtn = () => {
    toggle.setAttribute('aria-pressed', String(userPaused));
    toggle.setAttribute('aria-label', userPaused ? 'Play background video' : 'Pause background video');
    $('use', toggle)?.setAttribute('href', userPaused ? '#i-play' : '#i-pause');
  };
  toggle.hidden = false;
  setBtn();
  toggle.addEventListener('click', () => {
    userPaused = !userPaused;
    setBtn();
    if (userPaused) v.pause(); else { if (!v.src) v.src = v.dataset.src; v.play().catch(() => {}); }
  });
  new IntersectionObserver(([e]) => {
    if (e.isIntersecting) {
      if (!v.src) v.src = v.dataset.src;
      if (!userPaused) v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, { rootMargin: '200px 0px', threshold: 0.01 }).observe(band);
}

// ---------------------------------------------------------------------------
// Scroll motion (GSAP, self-hosted). Desktop collections run is pinned.
// ---------------------------------------------------------------------------
function initMotion() {
  const { gsap, ScrollTrigger } = window;
  if (reduce || !gsap || !ScrollTrigger) return;
  gsap.registerPlugin(ScrollTrigger);

  // Hero intro is a CSS animation (home.css) so it never waits for this script.

  const chase = $('[data-chase]');
  const mm = gsap.matchMedia();
  if (chase) {
    mm.add('(min-width: 861px)', () => {
      const track = $('[data-chase-track]');
      const panels = gsap.utils.toArray('[data-chase-track] .panel');
      const glow = $('[data-chase-glow]');
      const no = $('[data-chase-no]');
      const bar = $('[data-chase-bar]');
      const dist = () => track.scrollWidth - innerWidth;
      if (panels.length < 2) return;

      const run = gsap.to(track, {
        x: () => -dist(),
        ease: 'none',
        scrollTrigger: {
          trigger: chase, pin: true, start: 'top top', end: () => `+=${dist()}`, scrub: 1, invalidateOnRefresh: true,
          onUpdate: (self) => {
            const i = Math.min(panels.length - 1, Math.round(self.progress * (panels.length - 1)));
            gsap.set(bar, { scaleX: self.progress });
            no.textContent = String(i + 1).padStart(2, '0');
            glow.style.setProperty('--c', panels[i].dataset.c);
          },
        },
      });
      gsap.to('[data-chase-mark]', { x: () => -innerWidth * 0.9, ease: 'none',
        scrollTrigger: { trigger: chase, start: 'top top', end: () => `+=${dist()}`, scrub: true, invalidateOnRefresh: true } });

      panels.forEach((p, i) => {
        const st = i === 0 ? { trigger: chase, start: 'top 60%' } : { trigger: p, containerAnimation: run, start: 'left 65%' };
        gsap.from(p.querySelectorAll('.panel__name span'), { yPercent: 100, autoAlpha: 0, duration: 0.9, stagger: 0.08, ease: 'power4.out', scrollTrigger: st });
        gsap.from(p.querySelectorAll('.panel__desc, .panel__chips, .panel .btn'), { y: 24, autoAlpha: 0, duration: 0.8, stagger: 0.08, delay: 0.15, ease: 'power3.out', scrollTrigger: st });
        const scrub = i === 0 ? { trigger: chase, start: 'top bottom', end: 'top top', scrub: true }
          : { trigger: p, containerAnimation: run, start: 'left right', end: 'left left', scrub: true };
        const img = p.querySelector('.panel__img img');
        if (img) gsap.from(img, { scale: 1.2, ease: 'none', scrollTrigger: scrub });
        const b = p.querySelector('.panel__img--b');
        if (b) gsap.from(b, { yPercent: 40, rotate: 4, ease: 'none', scrollTrigger: { ...scrub } });
      });
    });
  }

  gsap.utils.toArray('.home .rail__head').forEach((h) => {
    gsap.from(h, { y: 24, autoAlpha: 0, duration: 0.8, ease: 'power3.out', scrollTrigger: { trigger: h, start: 'top 88%' } });
  });
  ScrollTrigger.batch('.home .rail .card', {
    start: 'top 92%',
    once: true,
    onEnter: (els) => gsap.from(els, { y: 40, autoAlpha: 0, duration: 0.9, stagger: 0.08, ease: 'power3.out' }),
  });
  gsap.utils.toArray('.home .reveal').forEach((el) => {
    gsap.timeline({ scrollTrigger: { trigger: el, start: 'top 85%' } })
      .from(el, { clipPath: 'inset(100% 0 0 0)', duration: 1.2, ease: 'power4.inOut' })
      .from(el.querySelector('img'), { scale: 1.2, duration: 1.6, ease: 'power3.out' }, 0);
  });
  if ($('[data-band]')) {
    gsap.to('[data-band] .img', { yPercent: 10, ease: 'none', scrollTrigger: { trigger: '[data-band]', start: 'top bottom', end: 'bottom top', scrub: true } });
    gsap.from('[data-band] .w', { yPercent: 60, autoAlpha: 0, duration: 0.9, stagger: 0.08, ease: 'power3.out', scrollTrigger: { trigger: '[data-band]', start: 'top 60%' } });
  }
  if ($('.rows')) {
    gsap.from('.rows li', { x: -30, autoAlpha: 0, duration: 0.7, stagger: 0.08, ease: 'power3.out', scrollTrigger: { trigger: '.rows', start: 'top 80%' } });
  }
  addEventListener('load', () => ScrollTrigger.refresh());
}

initHero();
initRails();
initBand();
// GSAP loads as deferred classic scripts; wait for them if this module runs first.
if (window.gsap) initMotion(); else addEventListener('load', initMotion, { once: true });
